<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import CopyValue from "@/components/CopyValue.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { taskStore } from "@/_store/taskStore";
import { fileUtils } from "@/utils";
import { courierList } from "@/maestros/courierValues";
import {
  formatCourier,
  formatHandlingType,
  formatTaskContentDescriptionTooltip,
  formatTaskDeliveryInfoSummary,
  formatVolume,
  formatWeight,
  hasTaskContentDescription,
  hasTaskDeliveryInfoSummary,
} from "../../../../utils/formatters";
import TaskDetailModal from "@/views/bultos/components/TaskDetailModal.vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false,
  },
  planId: {
    type: String,
    default: "",
  },
});

const tasksStore = taskStore();
const tableStore = argonTableStore();
const savedFilters = ref([]);
const showTaskDetailModal = ref(false);
const selectedTaskId = ref(null);
const statusOptions = Object.entries(ESTATUS_ENUM.task).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);
const columns = Object.freeze([
  { header: "Código de barras", field: "barcode", sort: false, position: "start" },
  { header: "Referencia", field: "shipment_reference", sort: false, position: "start" },
  { header: "Número parada", field: "stop_number", sort: false, position: "start" },
  { header: "Medidas", field: "measurements", sort: false, position: "start" },
  { header: "Peso", field: "weight", sort: false, position: "start" },
  { header: "Manipulación", field: "handling_type", sort: false, position: "start" },
  { header: "Naturaleza", field: "content_description", sort: false, position: "center" },
  { header: "Transportista", field: "courier", sort: false, position: "start" },
  { header: "Info reparto", field: "delivery_info.summary", sort: false, position: "center" },
  { header: "Estado", field: "status", sort: false, position: "center", width: "w-1" },
]);
const fieldFilters = Object.freeze([
  { field: "barcode", description: "Código de barras", exact: true },
  { field: "shipment_reference", description: "Referencia", exact: true },
  { field: "stop_number", description: "Número parada", exact: true },
]);

const defaultFilters = Object.freeze([
  {
    field: "delivery_info.status",
    label: "Estado",
    multi: true,
    options: statusOptions,
  },
  {
    field: "courier",
    label: "Transportista",
    multi: false,
    options: courierList,
  },
]);

const taskRows = computed(() => {
  const source = getTasksSource(tasksStore.tasks);
  const data = source.map((item) => ({
    ...item,
    _id: item._id,
  }));
  const totalFromResponse = Number.parseInt(tasksStore.tasks?.total, 10);
  const total = Number.isFinite(totalFromResponse) ? totalFromResponse : data.length;

  return {
    total,
    data,
  };
});
const tableItems = computed(() => taskRows.value);

onMounted(() => {
  tableStore.clear();
  tableStore.pagination.limit = 1000;
  tableStore.changeFilters(savedFilters.value);
});

watch(
  () => [props.isActive, props.planId],
  ([isActive]) => {
    if (!isActive) return;
    tableStore.clearSelection();
    tableStore.changeFilters(savedFilters.value);
    fetchTasks();
  },
  { immediate: true }
);

watch(
  () => tableStore.filters,
  (value) => {
    if (!props.isActive) return;
    savedFilters.value = Array.isArray(value)
      ? JSON.parse(JSON.stringify(value))
      : [];
  },
  { deep: true }
);

function getPosition(field) {
  const column = columns.find((col) => col.field === field);
  return column?.position || "start";
}

function getTasksSource(tasksValue) {
  if (Array.isArray(tasksValue?.data)) return tasksValue.data;
  if (Array.isArray(tasksValue)) return tasksValue;
  return [];
}

function getTaskStatusTooltip(item) {
  const status = String(item?.delivery_info?.status || "").toUpperCase();
  const description = String(item?.delivery_info?.status_description || "").trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
}

function openTaskDetailModal(taskItem) {
  const taskId = taskItem?._id ?? "";
  if (!taskId) return;
  selectedTaskId.value = String(taskId);
  showTaskDetailModal.value = true;
}

function closeTaskDetailModal() {
  showTaskDetailModal.value = false;
  selectedTaskId.value = null;
}

function fetchTasks() {
  if (!props.planId) {
    tasksStore.tasks = { total: 0, data: [] };
    return;
  }

  tasksStore.getTasks(
    tableStore.pagination,
    {
      ...(tableStore.query || {}),
      plan_id: props.planId,
    },
    tableStore.sort
  );
}

function reloadPage() {
  if (!props.isActive) return;
  fetchTasks();
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

async function exportTasks() {
  const bytes = await tasksStore.exportar(
    tableStore.pagination,
    {
      ...(tableStore.query || {}),
      plan_id: props.planId,
    },
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}
</script>

<template>
  <section class="card h-100 d-flex flex-column">
    <argon-table
      :columns="columns"
      :items="tableItems"
      :field-filters="fieldFilters"
      :default-filters="defaultFilters"      class="p-2 pb-0 flex-grow-1"
      :show-new-button="false"
      :show-check-input="false"
      :show-importar="false"
      :table-hover="false"
      @filter-changed="reloadPage"
      @on-reload="reloadPage"
      @sort-changed="onSortChanged"
      @page-changed="reloadPage"
      @on-click="openTaskDetailModal"
      @export-data="exportTasks"
    >
      <template #default="{ item }">
        <argon-td
          :text-primary="item.barcode"
          :position="getPosition('barcode')"
        >
          <template v-if="item.barcode" #append>
            <copy-value :value-to-copy="item.barcode" size="xs" show-on-hover />
          </template>
        </argon-td>
        <argon-td
          :text-primary="item.shipment_reference"
          :position="getPosition('shipment_reference')"
        >
          <template v-if="item.shipment_reference" #append>
            <copy-value :value-to-copy="item.shipment_reference" size="xs" show-on-hover />
          </template>
        </argon-td>
        <argon-td
          :text-primary="item.stop_number ?? ''"
          :position="getPosition('stop_number')"
        >
          <template v-if="item.stop_number" #append>
            <copy-value :value-to-copy="String(item.stop_number)" size="xs" show-on-hover />
          </template>
        </argon-td>
        <argon-td
          :text-primary="item.measurements"
          :position="getPosition('measurements')"
        />
        <argon-td
          :text-primary="formatWeight(item.weight)"
          :text-secondary="formatVolume(item.volumetric_weight, 3)"
          :position="getPosition('weight')"
        />
        <argon-td
          :text-primary="formatHandlingType(item.handling_type)"
          :position="getPosition('handling_type')"
        />
        <argon-td
          :position="getPosition('content_description')"
        >
          <i
            v-if="hasTaskContentDescription(item)"
            v-tooltip="formatTaskContentDescriptionTooltip(item)"
            class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
            aria-label="Ver naturaleza de la mercancía"
          />
        </argon-td>
        <argon-td
          :text-primary="formatCourier(item.courier)"
          :position="getPosition('courier')"
        />
        <argon-td
          :position="getPosition('delivery_info.summary')"
        >
          <i
            v-if="hasTaskDeliveryInfoSummary(item)"
            v-tooltip="formatTaskDeliveryInfoSummary(item)"
            class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
            aria-label="Ver info reparto"
          />
        </argon-td>
        <argon-td :position="getPosition('status')">
          <argon-status
            entity="task"
            :status="item.delivery_info?.status"
            :tooltip-external-text="getTaskStatusTooltip(item)"
            width="100px"
          />
        </argon-td>
      </template>
    </argon-table>
  </section>

  <task-detail-modal
    v-if="showTaskDetailModal && selectedTaskId"
    modal-id="plan-kanban-task-detail-modal"
    :task-id="selectedTaskId"
    @close="closeTaskDetailModal"
  />
</template>
